<script setup lang="ts">
import { RouterLink } from 'vue-router'
</script>

<template>
  <div class="min-h-screen bg-gradient-to-br from-blue-50 via-white to-indigo-50">
    <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      <!-- Hero Section -->
      <div class="text-center mb-16">
        <div class="inline-flex items-center px-4 py-2 bg-blue-100 text-blue-800 rounded-full text-sm font-medium mb-6">
          <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 10V3L4 14h7v7l9-11h-7z"></path>
          </svg>
          Professional Delivery Management
        </div>
        <h1 class="text-5xl md:text-6xl font-bold text-gray-900 mb-6 leading-tight">
          Delivery Area
          <span class="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-indigo-600">Manager</span>
        </h1>
        <p class="text-xl md:text-2xl text-gray-600 max-w-4xl mx-auto leading-relaxed">
          Streamline your ingredient inventory, process orders efficiently, and optimize delivery routes with intelligent routing systems.
        </p>
      </div>

      <!-- Feature Cards -->
      <div class="grid md:grid-cols-3 gap-8 mb-16">
        <!-- Inventory Management Card -->
        <div class="group bg-white rounded-2xl shadow-sm border border-gray-200 p-8 hover:shadow-xl hover:border-purple-200 transition-all duration-300 transform hover:-translate-y-1">
          <div class="flex items-center mb-6">
            <div class="bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl p-4 mr-5 group-hover:scale-110 transition-transform duration-300">
              <svg class="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4"></path>
              </svg>
            </div>
            <h2 class="text-2xl font-bold text-gray-900">Manage Inventory</h2>
          </div>
          <p class="text-gray-600 mb-6 leading-relaxed">
            Add and manage ingredient packs with different sizes, prices, and stock levels across multiple depot locations with real-time tracking.
          </p>
          <RouterLink 
            to="/inventory" 
            class="group inline-flex items-center px-6 py-3 bg-gradient-to-r from-purple-600 to-purple-700 text-white font-medium rounded-xl hover:from-purple-700 hover:to-purple-800 transition-all duration-200 transform hover:scale-105"
          >
            Manage Inventory
            <svg class="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform duration-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"></path>
            </svg>
          </RouterLink>
        </div>

        <!-- Add Order Card -->
        <div class="group bg-white rounded-2xl shadow-sm border border-gray-200 p-8 hover:shadow-xl hover:border-blue-200 transition-all duration-300 transform hover:-translate-y-1">
          <div class="flex items-center mb-6">
            <div class="bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl p-4 mr-5 group-hover:scale-110 transition-transform duration-300">
              <svg class="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6"></path>
              </svg>
            </div>
            <h2 class="text-2xl font-bold text-gray-900">Add New Order</h2>
          </div>
          <p class="text-gray-600 mb-6 leading-relaxed">
            Create new delivery orders with postcodes, phone numbers, and ingredient items. Inventory is automatically decremented upon order creation.
          </p>
          <RouterLink 
            to="/add" 
            class="group inline-flex items-center px-6 py-3 bg-gradient-to-r from-blue-600 to-blue-700 text-white font-medium rounded-xl hover:from-blue-700 hover:to-blue-800 transition-all duration-200 transform hover:scale-105"
          >
            Add Order
            <svg class="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform duration-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"></path>
            </svg>
          </RouterLink>
        </div>

        <!-- Active Orders Card -->
        <div class="group bg-white rounded-2xl shadow-sm border border-gray-200 p-8 hover:shadow-xl hover:border-green-200 transition-all duration-300 transform hover:-translate-y-1">
          <div class="flex items-center mb-6">
            <div class="bg-gradient-to-br from-green-500 to-green-600 rounded-xl p-4 mr-5 group-hover:scale-110 transition-transform duration-300">
              <svg class="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7"></path>
              </svg>
            </div>
            <h2 class="text-2xl font-bold text-gray-900">Order Management</h2>
          </div>
          <p class="text-gray-600 mb-6 leading-relaxed">
            View active delivery orders and analyze order history with profit tracking. Routes are intelligently optimized for maximum efficiency.
          </p>
          <div class="flex flex-col sm:flex-row gap-3">
            <RouterLink 
              to="/orders" 
              class="group inline-flex items-center justify-center px-6 py-3 bg-gradient-to-r from-green-600 to-green-700 text-white font-medium rounded-xl hover:from-green-700 hover:to-green-800 transition-all duration-200 transform hover:scale-105"
            >
              Active Orders
              <svg class="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform duration-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"></path>
              </svg>
            </RouterLink>
          </div>
        </div>
      </div>

      <!-- How It Works Section -->
      <div class="bg-white rounded-3xl shadow-sm border border-gray-200 p-8 md:p-12">
        <div class="text-center mb-12">
          <h3 class="text-3xl font-bold text-gray-900 mb-4">How It Works</h3>
          <p class="text-lg text-gray-600 max-w-2xl mx-auto">
            Streamlined workflow from inventory to delivery in three simple steps
          </p>
        </div>
        
        <div class="grid md:grid-cols-3 gap-8">
          <div class="text-center group">
            <div class="relative mb-6">
              <div class="w-20 h-20 bg-gradient-to-br from-blue-500 to-blue-600 rounded-2xl flex items-center justify-center mx-auto group-hover:scale-110 transition-transform duration-300">
                <span class="text-2xl font-bold text-white">1</span>
              </div>
              <div class="absolute -bottom-2 -right-2 w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                <svg class="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4"></path>
                </svg>
              </div>
            </div>
            <h4 class="text-xl font-bold text-gray-900 mb-3">Create Orders</h4>
            <p class="text-gray-600 leading-relaxed">
              Add customer details and select ingredient packs from your inventory. Real-time stock validation ensures order accuracy.
            </p>
          </div>

          <div class="text-center group">
            <div class="relative mb-6">
              <div class="w-20 h-20 bg-gradient-to-br from-green-500 to-green-600 rounded-2xl flex items-center justify-center mx-auto group-hover:scale-110 transition-transform duration-300">
                <span class="text-2xl font-bold text-white">2</span>
              </div>
              <div class="absolute -bottom-2 -right-2 w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                <svg class="w-4 h-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7"></path>
                </svg>
              </div>
            </div>
            <h4 class="text-xl font-bold text-gray-900 mb-3">Route Optimization</h4>
            <p class="text-gray-600 leading-relaxed">
              Advanced routing algorithms automatically optimize delivery sequences for maximum efficiency, reducing travel time and fuel costs.
            </p>
          </div>

          <div class="text-center group">
            <div class="relative mb-6">
              <div class="w-20 h-20 bg-gradient-to-br from-purple-500 to-purple-600 rounded-2xl flex items-center justify-center mx-auto group-hover:scale-110 transition-transform duration-300">
                <span class="text-2xl font-bold text-white">3</span>
              </div>
              <div class="absolute -bottom-2 -right-2 w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center">
                <svg class="w-4 h-4 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"></path>
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"></path>
                </svg>
              </div>
            </div>
            <h4 class="text-xl font-bold text-gray-900 mb-3">Navigate & Deliver</h4>
            <p class="text-gray-600 leading-relaxed">
              Use Apple Maps integration for turn-by-turn directions. Mark deliveries complete with one-click tracking.
            </p>
          </div>
        </div>
      </div>

      <!-- Feature Highlights -->
      <div class="mt-16 grid md:grid-cols-2 gap-8">
        <div class="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-2xl p-8 border border-blue-100">
          <div class="flex items-center mb-4">
            <div class="w-12 h-12 bg-blue-500 rounded-xl flex items-center justify-center mr-4">
              <svg class="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 10V3L4 14h7v7l9-11h-7z"></path>
              </svg>
            </div>
            <h3 class="text-xl font-bold text-gray-900">Real-time Processing</h3>
          </div>
          <p class="text-gray-600">
            Lightning-fast response times with global edge distribution for optimal performance worldwide.
          </p>
        </div>

        <div class="bg-gradient-to-br from-green-50 to-emerald-50 rounded-2xl p-8 border border-green-100">
          <div class="flex items-center mb-4">
            <div class="w-12 h-12 bg-green-500 rounded-xl flex items-center justify-center mr-4">
              <svg class="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"></path>
              </svg>
            </div>
            <h3 class="text-xl font-bold text-gray-900">Enterprise Ready</h3>
          </div>
          <p class="text-gray-600">
            Built with comprehensive error handling, robust security, and production-ready architecture.
          </p>
        </div>
      </div>
    </div>
  </div>
</template>
